import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { getProvider, wrapEthers, hasWallet } from '../utils/wallet';
import { switchNetwork, getCurrentChainId, isOnCorrectNetwork } from '../utils/network';
import { CURRENT_NETWORK } from '../config/network';
import { useNotify } from './NotificationContext';

const WalletContext = createContext(null);

export const useWallet = () => {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error('useWallet must be used within WalletProvider');
  return ctx;
};

export const WalletProvider = ({ children }) => {
  const notify = useNotify();

  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [provider, setProvider] = useState(null); // ethers Web3Provider
  const [signer, setSigner] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [wrongNetwork, setWrongNetwork] = useState(false);

  // Refs to avoid stale closures without causing effect loops
  const rawProviderRef = useRef(null);
  const listenersAttachedRef = useRef(false);
  const switchingRef = useRef(false);
  const notifyRef = useRef(notify);
  useEffect(() => { notifyRef.current = notify; });

  // Attach listeners exactly once when a raw provider becomes available
  const attachListeners = (raw) => {
    if (!raw || listenersAttachedRef.current) return;

    const handleAccountsChanged = (accounts) => {
      if (!accounts || accounts.length === 0) {
        disconnect();
        notifyRef.current.info('Wallet disconnected');
      } else {
        const newAcc = ethers.utils.getAddress(accounts[0]);
        setAccount(newAcc);
        const web3 = wrapEthers(raw);
        setProvider(web3);
        setSigner(web3.getSigner());
      }
    };

    const handleChainChanged = async (chainIdHex) => {
      const newChainId = parseInt(chainIdHex, 16);
      setChainId(newChainId);
      const correct = isOnCorrectNetwork(newChainId);
      setWrongNetwork(!correct);

      // Refresh provider so ethers picks up new network without page reload
      const web3 = wrapEthers(raw);
      setProvider(web3);
      if (account) setSigner(web3.getSigner());

      if (!correct && !switchingRef.current) {
        // Try to auto-switch back
        switchingRef.current = true;
        try {
          const ok = await switchNetwork(raw);
          if (!ok) {
            notifyRef.current.error(`Please switch to ${CURRENT_NETWORK.name} network`);
          }
        } catch (e) {
          notifyRef.current.error('Failed to switch network. Please switch manually.');
        } finally {
          switchingRef.current = false;
        }
      }
    };

    const handleDisconnect = () => {
      disconnect();
    };

    raw.on?.('accountsChanged', handleAccountsChanged);
    raw.on?.('chainChanged', handleChainChanged);
    raw.on?.('disconnect', handleDisconnect);
    listenersAttachedRef.current = true;
  };

  const connect = async () => {
    if (connecting) return;

    const raw = getProvider();
    if (!raw) {
      notify.error('No wallet detected. Please install Trust Wallet or MetaMask.');
      return;
    }

    setConnecting(true);
    rawProviderRef.current = raw;

    try {
      const accounts = await raw.request({ method: 'eth_requestAccounts' });
      if (!accounts || accounts.length === 0) {
        notify.error('No account found');
        setConnecting(false);
        return;
      }

      const acc = ethers.utils.getAddress(accounts[0]);
      let currentChain = await getCurrentChainId(raw);

      // If not on correct network, silently auto-switch
      if (!isOnCorrectNetwork(currentChain)) {
        switchingRef.current = true;
        try {
          const ok = await switchNetwork(raw);
          if (!ok) {
            notify.error(`Please switch to ${CURRENT_NETWORK.name} network to continue`);
            setWrongNetwork(true);
            setChainId(currentChain);
            setAccount(acc);
            attachListeners(raw);
            setConnecting(false);
            return;
          }
          currentChain = await getCurrentChainId(raw);
        } catch (e) {
          notify.error('Failed to switch network');
          setConnecting(false);
          return;
        } finally {
          switchingRef.current = false;
        }
      }

      const web3 = wrapEthers(raw);
      setProvider(web3);
      setSigner(web3.getSigner());
      setAccount(acc);
      setChainId(currentChain);
      setWrongNetwork(!isOnCorrectNetwork(currentChain));
      attachListeners(raw);

      notify.success('Wallet connected');

      // Persist so we can auto-reconnect
      try { localStorage.setItem('dlw_connected', '1'); } catch (_) {}
    } catch (err) {
      if (err?.code === 4001) {
        notify.error('Connection rejected');
      } else {
        notify.error(err?.message?.slice(0, 100) || 'Failed to connect');
      }
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = () => {
    setAccount(null);
    setChainId(null);
    setProvider(null);
    setSigner(null);
    setWrongNetwork(false);
    try { localStorage.removeItem('dlw_connected'); } catch (_) {}
  };

  const manualSwitchNetwork = async () => {
    const raw = rawProviderRef.current || getProvider();
    if (!raw) {
      notify.error('No wallet detected');
      return;
    }
    try {
      const ok = await switchNetwork(raw);
      if (ok) notify.success('Network switched');
      else notify.error('Network switch rejected');
    } catch (e) {
      notify.error('Failed to switch network');
    }
  };

  // Auto-reconnect on load if previously connected. Runs once.
  useEffect(() => {
    let cancelled = false;
    const autoReconnect = async () => {
      if (typeof window === 'undefined') return;
      try {
        if (localStorage.getItem('dlw_connected') !== '1') return;
      } catch (_) { return; }

      const raw = getProvider();
      if (!raw) return;

      try {
        const accounts = await raw.request({ method: 'eth_accounts' });
        if (cancelled || !accounts || accounts.length === 0) return;

        const acc = ethers.utils.getAddress(accounts[0]);
        rawProviderRef.current = raw;
        let currentChain = await getCurrentChainId(raw);

        if (!isOnCorrectNetwork(currentChain)) {
          switchingRef.current = true;
          try {
            await switchNetwork(raw);
            currentChain = await getCurrentChainId(raw);
          } catch (_) { /* ignore */ }
          switchingRef.current = false;
        }

        if (cancelled) return;
        const web3 = wrapEthers(raw);
        setProvider(web3);
        setSigner(web3.getSigner());
        setAccount(acc);
        setChainId(currentChain);
        setWrongNetwork(!isOnCorrectNetwork(currentChain));
        attachListeners(raw);
      } catch (_) { /* ignore */ }
    };
    autoReconnect();
    return () => { cancelled = true; };
  }, []);

  const value = {
    account,
    chainId,
    provider,
    signer,
    connecting,
    wrongNetwork,
    connected: !!account,
    hasWallet: hasWallet(),
    connect,
    disconnect,
    switchNetwork: manualSwitchNetwork
  };

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
};
